if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      const swiperOptions =
        JSON.parse(this.getAttribute('data-swiper-options')) || {};
      const tabletMinWidth = Number(swiperOptions.tabletMinWidth) || 750;
      const desktopMinWidth = Number(swiperOptions.desktopMinWidth) || 1100;

      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent = this.closest('[data-section-id]');
      this.sliderOptions = {
        a11y: false,
        threshold: 3,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: swiperOptions.slidesPerViewMobile || 'auto',
        resistanceRatio: 0.72,
        breakpoints: {
          [tabletMinWidth]: {
            slidesPerView: swiperOptions.slidesPerViewTablet || 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          },
          [desktopMinWidth]: {
            slidesPerView: swiperOptions.slidesPerViewDesktop || 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          }
        }
      };
      if (swiperOptions.navigation) {
        this.sliderOptions.navigation = {
          ...swiperOptions.navigation
        };
      }
      if (swiperOptions.autoplay) {
        this.sliderOptions.autoplay = {
          ...swiperOptions.autoplay
        };
      }
      if (swiperOptions.loop) {
        this.sliderOptions.loop = swiperOptions.loop;
      }
    }

    connectedCallback() {
      const boot = () => {
        if (this.booted) return;
        this.booted = true;
        if (this.requiresFetch) {
          this.fetchItems();
          return;
        }
        this.cleanRecommendationUrls();
        this.initSlider();
        this.syncHeights();
        window.addEventListener("resize", () => this.syncHeights());
      };

      // Never defer the fetch path: fetched content is what un-hides these
      // sliders (and their hidden containers, e.g. "Complete the set" via the
      // ComplementaryProducts subclass) — a display:none element never
      // intersects, so deferring would deadlock. Swiper init still only runs
      // after the fetch response, so the eager fetch costs little.
      if (Shopify.designMode || !window.deferUntilNearViewport || this.requiresFetch) {
        boot();
      } else {
        window.deferUntilNearViewport(this, boot);
      }
    }

    syncHeights() {
      const textSlide = this.querySelector('.products-feed__card-text-custom-slide');
      const actionElement = this.querySelector('.product-card__actions');

      if (textSlide && actionElement) {
        const actionHeight = actionElement.offsetHeight;
        textSlide.style.height = `${actionHeight}px`;
      }
    }

    fetchItems() {
      if (!this.parent) throw new Error('Parent is missing');
      fetch(this.getAttribute('data-url'))
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(
            text,
            'text/html'
          );
          const wrapper = html.querySelector('.swiper-wrapper');

          if (wrapper && wrapper.innerHTML.trim().length) {
            this.querySelector('.swiper-wrapper').innerHTML =
              wrapper.innerHTML;
            this.cleanRecommendationUrls();
            this.parent.classList.remove('hidden');
            this.initSlider();
          }
        })
        .catch(e => {
          console.error(e);
        });
    }

    cleanRecommendationUrls() {
      // Strip recommendation tracking params (pr_*) and force a trailing
      // slash on product/collection paths so card hrefs match the canonical.
      this.querySelectorAll('a[href]').forEach((link) => {
        const href = link.getAttribute('href');
        if (!href) return;
        try {
          const url = new URL(href, window.location.origin);
          let mutated = false;
          [...url.searchParams.keys()].forEach((key) => {
            if (key.startsWith('pr_')) {
              url.searchParams.delete(key);
              mutated = true;
            }
          });
          if (
            !url.pathname.endsWith('/') &&
            !url.pathname.includes('.') &&
            !url.pathname.includes('#')
          ) {
            url.pathname += '/';
            mutated = true;
          }
          if (mutated) {
            const search = url.searchParams.toString();
            const cleanPath = url.pathname + (search ? '?' + search : '') + url.hash;
            link.setAttribute('href', cleanPath);
          }
        } catch (e) {
          // ignore malformed URLs
        }
      });
    }

    initSlider(slider = this) {
      this.slider = new Swiper(slider, this.sliderOptions);

      this.slider.wrapperEl.setAttribute('aria-live', 'polite');
      this.slider.wrapperEl.id = `swiper-wrapper-${this.dataset.sectionId}`;
      this.slider.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute(
          'aria-label',
          `${i + 1} / ${this.slider.slides.length}`
        );
        slide.dataset.index = i;
      });

      if (this.swiperOptions?.navigation) {
        const sliderButtons = [
          this.slider.navigation.prevEl,
          this.slider.navigation.nextEl
        ];

        sliderButtons.forEach((button, i) => {
          let type = 'Previous';

          if (i === 1) {
            type = 'Next';
          }

          button.setAttribute('aria-label', `${type} slide`);
          button.setAttribute(
            'aria-controls',
            this.slider.wrapperEl.id
          );
        });
      }

      this.slider.wrapperEl.addEventListener('focusin', e => {
        this.slider.el.scrollLeft = 0;

        const slide = e.target.closest('.swiper-slide');
        const slideIndex = +slide.dataset.index;
        const isFirstSlide = slideIndex === 0;
        const isLastSlide =
          slideIndex === this.slider.slides.length - 1;

        if (isFirstSlide || isLastSlide) {
          this.slider.slideTo(slideIndex, 0);

          return;
        }

        const slideRect = slide.getBoundingClientRect();
        const wrapperWidth = this.slider.wrapperEl.clientWidth;
        // prettier-ignore
        const slideLeft = slideRect.x + slideRect.width - ((document.body.clientWidth - wrapperWidth) / 2);
        const isSlideVisible =
          slideLeft <= wrapperWidth && slideLeft >= slideRect.width;

        if (isSlideVisible) {
          return;
        }

        slideLeft >= slideRect.width
          ? this.slider.slideNext(0)
          : this.slider.slidePrev(0);
      });

      const swiperOptions = JSON.parse(this.getAttribute('data-swiper-options')) || {};
      // Never auto-advance for visitors who ask for reduced motion, and hold the slider while
      // keyboard focus is inside it (WCAG 2.2.2).
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const canAutoplay = () =>
        this.sliderOptions.autoplay && !reduceMotion && !this.classList.contains('product--open-on-quick-cart');
      if (this.sliderOptions.autoplay && reduceMotion) this.slider.autoplay.stop();
      /** stop / start autoplay on hover if quick cart is not open */
      this.slider.wrapperEl.addEventListener('mouseenter', () =>
        this.slider.autoplay.stop()
      );
      this.slider.wrapperEl.addEventListener('mouseleave', () => {
        if (canAutoplay() && !this.contains(document.activeElement)) {
          this.slider.autoplay.start();
        }
        if (swiperOptions.autoplay === false) {
          this.slider.autoplay.stop();
        }
      });
      this.addEventListener('focusin', () => this.slider.autoplay.stop());
      this.addEventListener('focusout', event => {
        if (!this.contains(event.relatedTarget) && canAutoplay()) this.slider.autoplay.start();
      });

      this.initQuickCart();

      this.querySelectorAll('product-card').forEach(card => {
        card.init();
      });

      if (Shopify.designMode) {
        this.initQuickCart();
      }
    }

    /**
     * Rebind the quick-cart drawer to this slider's cards. quick-cart-drawer.js
     * is deferred and runs after a slider that filled itself from cache (Rebuy
     * recommendations on a repeat view), so the element may not be upgraded
     * yet and has no init() — calling it threw and skipped the product-card
     * init below. Skipping is safe: the drawer's own connectedCallback binds
     * every trigger present by the time it runs.
     */
    initQuickCart() {
      if (!this.hasAttribute('data-init-quick-cart') || !this.querySelector('.swiper-slide')) return;
      const drawer = document.querySelector('quick-cart-drawer');
      if (drawer && typeof drawer.init === 'function') drawer.init();
    }
  }

  customElements.define('card-product-slider', CardProductSlider);
}
